import { useState, useEffect } from 'react';
import { createStore } from '@resource-kit/controllers';

type Theme = 'light' | 'dark';

const STORAGE_KEY = 'theme';
const themeStore = createStore<Theme>({
  key: STORAGE_KEY,
  version: 1,
  scope: 'device',
  sanitize: (raw) => (raw === 'light' || raw === 'dark' ? raw : null),
});

function getInitialTheme(): Theme {
  const stored = themeStore.read();
  if (stored) return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme: Theme) {
  if (theme === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

// LIMITATION: single-mount only. Each `useTheme()` call owns independent
// `useState`, so two live instances can drift out of sync, and none listens
// to `matchMedia` OS-theme changes. Tolerable while there is a single mount
// point. Before using this at multiple points, promote it to a shared
// context (or a `matchMedia` + storage-event subscription).
export function useTheme() {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  useEffect(() => {
    applyTheme(theme);
    themeStore.write(theme);
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'));

  return { theme, setTheme, toggleTheme };
}
